import { Select } from '@mantine/core';
import { useField } from '@mantine/form';
import { modals } from '@mantine/modals';
import { IconReportMoney } from '@tabler/icons-react';
import { ChooseAdditionalInvoices } from 'app/Private/Clients/Detail/UnhandledInvoicesTab/ChooseAdditionalInvoices';
import { useInvoiceSelection } from 'app/Private/Clients/Detail/UnhandledInvoicesTab/use-invoice-selection';
import { iconProps } from 'components/icon-props';
import { Right } from 'components/Right';
import { SaveButton } from 'components/SaveButton';
import { tKeys, useTranslate } from 'locales/index';
import { useTranslation } from 'locales/logic';
import type { InvoiceAction, UnhandledInvoice } from 'modules/AtRisk/api/use-load-unhandled-client';
import { isNullish } from 'remeda';
import { GcollectModal } from 'shared/components/InvoiceActionsDropdown/GcollectModal';
import { useLoadThirdParties } from 'shared/hooks/use-load-third-parties';
import type { ActionButtonDefinition } from '../use-build-action-buttons';
import { computeCommonProps } from './common';
import { useThirdPartySend } from './use-third-party-send';

export function useDebtCollector(): (
  action: InvoiceAction,
  invoices: Array<UnhandledInvoice>,
) => ActionButtonDefinition {
  const { t } = useTranslation();
  const { selectedInvoiceIds } = useInvoiceSelection();

  return (action, invoices) => {
    const handler = () =>
      modals.open({
        size: 'lg',
        title: t(tKeys.DEBTOR.REACTION.SELECT_INVOICES),
        children: (
          <ChooseAdditionalInvoices
            selectedInvoiceIds={selectedInvoiceIds}
            unhandledInvoices={invoices}
            actionType="debt_collection"
            onNext={({ invoiceIds }) => {
              const unhandledIds = new Set(invoices.map(invoice => invoice.id));
              modals.closeAll();
              modals.open({
                size: 'md',
                title: t(tKeys.CHOOSE_DEBT_COLLECTOR),
                children: (
                  <ChoseDebtCollector
                    invoices={invoices.filter(invoice => invoiceIds.includes(invoice.id))}
                    extraInvoiceIds={invoiceIds.filter(id => !unhandledIds.has(id))}
                  />
                ),
              });
            }}
          />
        ),
      });

    return {
      icon: <IconReportMoney {...iconProps()} />,
      translationKey: tKeys.MODULES.AT_RISK.DEBT_COLLECTOR,
      onClick: handler,
      ...computeCommonProps(action, invoices),
    };
  };
}

type ChoseDebtCollectorProps = {
  invoices: Array<UnhandledInvoice>;
  extraInvoiceIds: Array<string>;
};

const ChoseDebtCollector = ({ invoices, extraInvoiceIds }: ChoseDebtCollectorProps) => {
  const t = useTranslate();

  const { invoiceIds, isSendToThirdPartyLoading, send } = useThirdPartySend(
    invoices,
    extraInvoiceIds,
  );
  const {
    thirdParties: { debtCollector },
    isThirdPartiesInitialLoading,
  } = useLoadThirdParties({ ids: invoiceIds.map(Number) });

  const selectedDebtCollector = useField<string | null>({ initialValue: '' });
  const selectedDebtCollectorId = selectedDebtCollector.getValue();
  const selectedDebtCollectorCompany = debtCollector.availableCompanies.find(
    company => String(company.id) === selectedDebtCollectorId,
  );

  const isGcollect = selectedDebtCollectorCompany?.isGcollect ?? false;

  const sendToDebtCollector = () => {
    if (isNullish(selectedDebtCollectorId)) return;

    if (isGcollect) {
      modals.open({
        title: t(tKeys.FOLLOW_UP.INVOICE_TO_PROCESS.GCOLLECT.CONFIRM_RATES),
        centered: true,
        size: 'xl',
        children: (
          <GcollectModal
            gcollectId={Number(selectedDebtCollectorId)}
            invoiceIds={invoiceIds.map(Number)}
          />
        ),
      });
      return;
    }

    send(selectedDebtCollectorId);
  };

  if (isThirdPartiesInitialLoading) return <div>{t(tKeys.LOADING)}</div>;

  return (
    <>
      <Select
        label={t(tKeys.SELECT_DEBT_COLLECTOR)}
        placeholder={t(tKeys.SELECT_DEBT_COLLECTOR)}
        data={debtCollector.availableCompanies.map(company => ({
          label: company.name,
          value: String(company.id),
        }))}
        {...selectedDebtCollector.getInputProps()}
      />
      <Right mt="md">
        <SaveButton
          label={t(tKeys.SUBMIT)}
          loading={isSendToThirdPartyLoading}
          disabled={selectedDebtCollector.getValue() === ''}
          onClick={sendToDebtCollector}
        />
      </Right>
    </>
  );
};
